import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useReactToPrint } from 'react-to-print';

import APIHandler from 'config/APIHandler';
import getFeatureFlags from 'helpers/getFeatureFlags';
import { useSettings } from 'hooks/useSettings';
import { clsx } from 'clsx';

const Diploma = ({ appId = -1, firstName = '', lastName = '', email = '' }) => {
  const [diplomaLoaded, setDiplomaLoaded] = useState(false);
  const [useEuServers, setUseEuServers] = useState(false);

  const isPrinting = useRef(false);
  const diplomaRef = useRef(null);

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      const flags = await getFeatureFlags();
      setUseEuServers(!!flags.eu_servers);
    };

    fetchFeatureFlags();
  }, []);

  const handleDiplomaLoaded = () => {
    setDiplomaLoaded((prev) => {
      if (!prev) {
        return true;
      }
      return prev;
    });
  };

  const {
    appInfo: { appName: name },
    salesCompanionAppId,
  } = useSettings();

  const image = useEuServers
    ? `https://eu.crm.travpromobile.com/diploma_api?firstName=${firstName}&lastName=${lastName}&appID=${appId}&email=${email}`
    : `https://cms.travpromobile.com/api/app/v2/diploma?first_name=${firstName}&last_name=${lastName}&app_id=${salesCompanionAppId}&email=${email}`;
  const subject = encodeURIComponent(`${name ?? ''} diploma`);
  const body = encodeURIComponent(image);

  const handleAfterPrint = () => {
    isPrinting.current = false;
  };

  const reactToPrintTrigger = useReactToPrint({
    content: () => diplomaRef.current,
    onAfterPrint: handleAfterPrint,
  });

  // prevents multiple concurrent print triggers
  const handlePrint = () => {
    if (isPrinting.current === false) {
      isPrinting.current = true;
      reactToPrintTrigger();
    }
  };

  return (
    <div id="diploma" className="fancybox-share diploma">
      {/* this is the printable part */}
      <div ref={diplomaRef}>
        <img className="diploma__image" name="diploma" src={image} alt="diploma" onLoad={handleDiplomaLoaded} />
      </div>

      {diplomaLoaded && (
        <div className="buttons__diploma">
          <div
            type="button"
            disabled={diplomaLoaded}
            onClick={handlePrint}
            className={clsx('diploma-share__button', 'fancybox-share__button', 'fancybox-share__button--fb')}
          >
            <FontAwesomeIcon icon={['fas', 'print']} />
            <span> {i18n.translate('reportCard.print')}</span>
          </div>

          <a
            href={`mailto:?subject=${subject}&body=${body}`}
            className="fancybox-share__button fancybox-share__button--pt"
          >
            <FontAwesomeIcon icon={['fas', 'at']} />
            <span>{i18n.translate('reportCard.email')}</span>
          </a>

          <a
            href={APIHandler.getDownloadURL(
              image,
              `${name} Diploma ${firstName} ${lastName}.png`.replaceAll(' ', '_'),
              'png',
            )}
            className="fancybox-download__button fancybox-download__button--pt"
          >
            <FontAwesomeIcon icon={['fas', 'download']} />
            <span>{i18n.translate('reportCard.download')}</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default Diploma;

Diploma.propTypes = {
  appId: PropTypes.number,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
};

Diploma.defaultProps = {
  appId: -1,
  firstName: '',
  lastName: '',
  email: '',
};
